import { Component } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { jsPDF } from "jspdf";

@Component({
  selector: "app-inspeccion-vehiculos",
  templateUrl: "./inspeccion-vehiculos.component.html",
  styleUrls: ["./inspeccion-vehiculos.component.scss"],
})
export class InspeccionVehiculosComponent {
  documentos = [
    {
      id: 1,
      nombre: "Procedimiento de Inspección Diaria de la Flota",
    },
  ];
  pdfUrl: SafeResourceUrl | null = null;
  pdfNombre: string | null = null;
  mostrarModal = false;

  private blobUrl: string | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  abrirDocumento(): void {
    this.generarProcedimiento();
  }

  private generarProcedimiento(): void {
    const pdf = new jsPDF();

    const margen = 15;
    const ancho = 180;
    const margenInferior = 270; // límite vertical antes de forzar salto de página
    const yInicioSeccion = 30;

    // --------------------------------------------------------
    // Paleta de colores
    // --------------------------------------------------------
    const colorPrimario: [number, number, number] = [24, 58, 92]; // azul corporativo
    const colorAcento: [number, number, number] = [0, 141, 137]; // verde azulado
    const colorTexto: [number, number, number] = [55, 55, 55];
    const colorTextoSuave: [number, number, number] = [110, 110, 110];
    const colorLinea: [number, number, number] = [215, 220, 224];

    const setColorTexto = (c: [number, number, number]) =>
      pdf.setTextColor(c[0], c[1], c[2]);
    const setColorRelleno = (c: [number, number, number]) =>
      pdf.setFillColor(c[0], c[1], c[2]);
    const setColorTrazo = (c: [number, number, number]) =>
      pdf.setDrawColor(c[0], c[1], c[2]);

    const nombreDocumento = "Procedimiento de Inspección Diaria de la Flota";

    // --------------------------------------------------------
    // Helpers de layout
    // --------------------------------------------------------

    // Si el contenido que sigue no cabe en lo que resta de la página,
    // agrega una página nueva y devuelve el "y" inicial de esa página.
    const verificarSalto = (y: number, espacioNecesario: number = 10): number => {
      if (y + espacioNecesario > margenInferior) {
        pdf.addPage();
        return yInicioSeccion;
      }
      return y;
    };

    const titulo = (texto: string, y: number = yInicioSeccion): number => {
      // Barra de acento a la izquierda del título
      setColorRelleno(colorAcento);
      pdf.rect(margen, y - 6, 3, 9, "F");

      setColorTexto(colorPrimario);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text(texto, margen + 7, y);

      // Línea divisoria debajo del título
      setColorTrazo(colorLinea);
      pdf.setLineWidth(0.4);
      pdf.line(margen, y + 4, margen + ancho, y + 4);

      setColorTexto(colorTexto);
      return y + 13;
    };

    const subtitulo = (texto: string, y: number): number => {
      y = verificarSalto(y, 16);
      setColorRelleno(colorAcento);
      pdf.circle(margen + 1.3, y - 1.6, 1.3, "F");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      setColorTexto(colorPrimario);
      pdf.text(texto, margen + 6, y);

      setColorTexto(colorTexto);
      return y + 8;
    };

    const parrafo = (texto: string, y: number): number => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      setColorTexto(colorTexto);

      const lineas = pdf.splitTextToSize(texto, ancho);
      const alturaTexto = lineas.length * 6;

      y = verificarSalto(y, alturaTexto);
      pdf.text(lineas, margen, y);

      return y + alturaTexto + 4;
    };

    const lista = (items: string[], y: number): number => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);

      items.forEach((item) => {
        y = verificarSalto(y, 8);

        const lineas = pdf.splitTextToSize(item, ancho - 12);

        // Punto de viñeta coloreado
        setColorRelleno(colorAcento);
        pdf.circle(margen + 2, y - 1.4, 1, "F");

        setColorTexto(colorTexto);
        pdf.text(lineas, margen + 7, y);
        y += lineas.length * 7;
      });

      return y + 3;
    };

    // Etiqueta corta en negrita (p. ej. "Conductor:") con color de acento
    const etiqueta = (texto: string, y: number): number => {
      y = verificarSalto(y, 10);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      setColorTexto(colorAcento);
      pdf.text(texto, margen, y);
      setColorTexto(colorTexto);
      return y + 7;
    };

    // Escribe el título de una sección de forma continua: si no hay espacio
    // suficiente en la página actual para el título + algo de contenido,
    // salta de página; si sí hay espacio, sigue en la misma página
    // (así se evitan hojas con mucho espacio en blanco).
    const nuevaSeccion = (textoTitulo: string, y: number): number => {
      y = verificarSalto(y, 40);
      return titulo(textoTitulo, y);
    };

    // ============================================================
    // PORTADA
    // ============================================================

    // Banda superior de color
    setColorRelleno(colorPrimario);
    pdf.rect(0, 0, 210, 55, "F");

    setColorRelleno(colorAcento);
    pdf.rect(0, 55, 210, 3, "F");

    setColorTexto([255, 255, 255]);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("PLAN ESTRATÉGICO DE SEGURIDAD VIAL - PESV", 105, 32, {
      align: "center",
    });

    // Título principal
    setColorTexto(colorPrimario);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text(
      pdf.splitTextToSize(
        "PROCEDIMIENTO PARA LA INSPECCIÓN DIARIA DE LA FLOTA",
        160
      ),
      105,
      100,
      { align: "center" }
    );

    setColorRelleno(colorAcento);
    pdf.rect(75, 112, 60, 1.2, "F");

    setColorTexto(colorAcento);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text("MEDIANTE LA APLICACIÓN MÓVIL CONTROLCHECK", 105, 124, {
      align: "center",
    });

    setColorTexto(colorTextoSuave);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text(
      "Control y trazabilidad del alistamiento vehicular previo a la operación",
      105,
      134,
      { align: "center" }
    );

    // Banda inferior de color con fecha de generación
    setColorRelleno(colorPrimario);
    pdf.rect(0, 277, 210, 20, "F");

    setColorTexto([255, 255, 255]);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    const fechaGeneracion = new Date().toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    pdf.text(`Documento generado el ${fechaGeneracion}`, 105, 289, {
      align: "center",
    });

    // Enlaces de descarga de la aplicación ControlCheck (centrados sobre el pie)
    const androidUrl = 'https://play.google.com/store/apps/details?id=com.indesoft.desarrollo.alistamiento&pli=1';
    const iosUrl = 'https://apps.apple.com/us/app/controlcheck/id6743885161';

    // Texto guía
    pdf.setFontSize(9);
    setColorTexto(colorAcento);
    pdf.text('Descarga la app ControlCheck:', 105, 252, { align: 'center' });

    // Enlaces clicables
    pdf.setFontSize(10);
    setColorTexto(colorPrimario);
    const androidText = 'Android (Google Play)';
    const iosText = 'iOS (App Store)';

    pdf.text(androidText, 105, 260, { align: 'center' });
    const androidWidth = pdf.getTextWidth(androidText);
    const androidHeight = 6;
    pdf.link(105 - androidWidth / 2, 260 - androidHeight + 2, androidWidth, androidHeight, { url: androidUrl });

    pdf.text(iosText, 105, 268, { align: 'center' });
    const iosWidth = pdf.getTextWidth(iosText);
    const iosHeight = 6;
    pdf.link(105 - iosWidth / 2, 268 - iosHeight + 2, iosWidth, iosHeight, { url: iosUrl });

    setColorTexto(colorTexto);

    // ============================================================
    // 1. OBJETIVO - ALCANCE - RESPONSABLES
    // ============================================================

    pdf.addPage(); // primera página de contenido, después de la portada
    let y = nuevaSeccion("1. Objetivo, Alcance y Responsables", yInicioSeccion);

    y = subtitulo("1.1 Objetivo", y);

    y = parrafo(
      "Establecer el procedimiento para realizar la inspección diaria de los vehículos y motocicletas de la organización antes del inicio de la operación, con el fin de identificar oportunamente condiciones que puedan afectar la seguridad vial, prevenir incidentes y garantizar el cumplimiento del Plan Estratégico de Seguridad Vial (PESV).",
      y
    );

    y = subtitulo("1.2 Alcance", y);

    y = parrafo(
      "Aplica a todos los vehículos y motocicletas propios, en leasing o afiliados que hagan parte de la operación de la organización, así como a todos los conductores autorizados para su operación.",
      y
    );

    y = subtitulo("1.3 Responsables", y);

    y = etiqueta("Conductor:", y);

    y = lista(
      [
        "Realizar la inspección diaria antes de iniciar la jornada.",
        "Registrar información veraz.",
        "Reportar condiciones inseguras.",
        "Adjuntar evidencias fotográficas.",
        "No iniciar operación cuando exista riesgo.",
      ],
      y
    );

    y = etiqueta("Supervisor o Coordinador:", y);

    y = lista(
      [
        "Revisar inspecciones.",
        "Evaluar novedades.",
        "Autorizar o inmovilizar el vehículo.",
        "Gestionar acciones correctivas.",
        "Realizar seguimiento.",
      ],
      y
    );

    y = etiqueta("Administrador de la aplicación:", y);

    y = lista(
      [
        "Mantener documentación actualizada.",
        "Garantizar funcionamiento.",
        "Administrar la data de los conductores y vehículos.",
         "Garantizar la integridad de la data de los conductores y vehículos.",
         
      ],
      y
    );

    // ============================================================
    // 2. PROCEDIMIENTO
    // ============================================================

    y = nuevaSeccion("2. Procedimiento", y);

    y = subtitulo("2.1 Inicio de sesión", y);

    y = parrafo(
      "El conductor accede mediante sus credenciales personales.",
      y
    );

    y = lista(["PLACA", "CONTRASEÑA"], y);

    y = subtitulo("2.2 Validación documental", y);

    y = parrafo(
      "La aplicación verifica automáticamente, la vigencia de la documentación del conductor y del vehículo. Si existen documentos vencidos o próximos a vencer genera una alerta.",
      y
    );

    y = subtitulo("2.3 Registro del kilometraje", y);

    y = parrafo(
      "El conductor registra el kilometraje actual para mantener el control del mantenimiento preventivo.",
      y
    );

    y = subtitulo("2.4 Validación de licencia", y);

    y = parrafo(
      "La aplicación valida la vigencia de la licencia de conducción y genera alertas cuando corresponda.",
      y
    );

    // ============================================================
    // 3. INSPECCIÓN PREOPERACIONAL
    // ============================================================

    y = nuevaSeccion("3. Inspección Preoperacional", y);

    y = parrafo(
      "El conductor diligencia el formulario digital de inspección Carro.",
      y
    );

    y = lista(
      [
        "LICENCIA DE TRÁNSITO",
        "TARJETA DE OPERACIÓN",
        "SOAT",
        "REVISIÓN TÉCNICO-MECÁNICA",
        "PÓLIZA RCC",
        "PÓLIZA RCE",
        "LICENCIA DE CONDUCCIÓN",
        "CAJA DE CAMBIOS / TRANSMISIÓN",
        "SUSPENSIÓN / DIRECCIÓN",
        "NIVEL DE AGUA, ACEITE, COMBUSTIBLE Y FLUIDOS",
        "FUGAS DEL MOTOR",
        "CORREAS",
        "CONEXIONES ELÉCTRICAS",
        "FILTROS HÚMEDOS Y SECOS",
        "ESPEJO CENTRAL CONVEXO",
        "ESPEJOS LATERALES",
        "ALARMA DE RETROCESO Y PITO",
        "TAPAS",
        "FRENO DE SERVICIO",
        "FRENO DE EMERGENCIA",
        "VIDRIO FRONTAL / LIMPIABRISAS",
        "BOTIQUÍN, EQUIPO DE CARRETERA",
        "EXTINTOR DE INCENDIOS",
        "ASIENTOS Y TAPICERÍA",
        "SENSORES DEL TABLERO: (KM/H, BATERÍA, TEMPERATURA, COMBUSTIBLE)",
        "ESTRIBOS",
        "BATERÍA, BORNES, CABLES, NIV. DE ELECTROLITO Y SULFATACIÓN",
        "LUCES EXTERIORES ALTAS Y BAJAS",
        "LUCES DIRECCIONALES DELANTERAS",
        "LUCES DIRECCIONALES TRASERAS",
        "LUCES DE ESTACIONAMIENTO",
        "LUCES DE STOP",
        "ESTADO GENERAL DE LAS LLANTAS",
        "PRESIÓN DEL AIRE",
        "LLANTA DE REPUESTOS",
      ],
      y
    );

    y = parrafo(
      "El conductor diligencia el formulario digital de inspección Motos.",
      y
    );

     y = lista(
      [
        "LICENCIA DE TRÁNSITO",
        "SOAT",
        "REVISIÓN TÉCNICO-MECÁNICA",
        "LICENCIA DE CONDUCCIÓN",
        "TRANSMISIÓN (CADENA, CORONA Y PIÑÓN)",
        "SUSPENSIÓN / DIRECCIÓN",
        "ACEITE DEL MOTOR Y COMBUSTIBLE",
        "FUGAS DE ACEITE O COMBUSTIBLE",
        "CADENA (TENSIÓN Y LUBRICACIÓN)",
        "CONEXIONES ELÉCTRICAS",
        "ESPEJO DERECHO",
        "ESPEJO IZQUIERDO",
        "PITO",
        "TAPAS Y CARENAJES",
        "FRENO DELANTERO",
        "FRENO TRASERO",
        "ASIENTO",
        "INDICADORES DEL TABLERO (VELOCÍMETRO, COMBUSTIBLE, TESTIGOS)",
        "ESTRIBERAS (REPOSA PIES)",
        "BATERÍA, BORNES Y CABLES",
        "CASCO",
        "VISOR DEL CASCO",
        "SOPORTE DE LA PLACA",
        "GUARDA BARROS",
        "PATAL LATERAL",
        "EMISIÓN DE GASES",
        "LUCES ALTAS Y BAJAS",
        "LUCES DIRECCIONALES DELANTERAS",
        "LUCES DIRECCIONALES TRASERAS",
        "LUCES DE ESTACIONAMIENTO",
        "LUCES DE STOP",
        "ESTADO GENERAL DE LAS LLANTAS",
        "PRESIÓN DEL AIRE",
      ],
      y
    );
    y = subtitulo("3.1 Registro de observaciones", y);

    y = lista(
      [
        "Descripción de la novedad",
        "Ubicación del daño",
        "Nivel de criticidad",
        "Observaciones",
      ],
      y
    );

    y = subtitulo("3.2 Registro de evidencias", y);

    y = parrafo(
      "La aplicación permite anexar fotografías u otras evidencias de la novedad encontrada.",
      y
    );

    // ============================================================
    // 4. EVALUACIÓN DE RESULTADOS
    // ============================================================

    y = nuevaSeccion("4. Evaluación de Resultados", y);

    y = parrafo(
      "Al finalizar la inspección la aplicación genera automáticamente el resultado del alistamiento.",
      y
    );

    y = lista(
      [
        "Resultado de inspección",
        "Novedades",
        "Notificación automática",
        "Estado de aprobación",
      ],
      y
    );

    y = parrafo(
      "Si la condición representa un riesgo para la seguridad vial, el vehículo no podrá iniciar operación hasta la corrección de la novedad.",
      y
    );

    // ============================================================
    // 5. CONSERVACIÓN DE LOS REGISTROS
    // ============================================================

    y = nuevaSeccion("5. Conservación de los Registros", y);

    y = lista(
      [
        "Registros de inspección",
        "Fecha y hora",
        "Conductor",
        "Vehículo",
        "Kilometraje",
        "Resultado",
        "Observaciones",
        "Fotografías",
        "Estado de aprobación",
        "Historial de novedades",
      ],
      y
    );

    y = subtitulo("Requisitos", y);

    y = lista(
      [
        "Almacenamiento digital.",
        "Integridad de la información.",
        "Disponibilidad.",
        "Trazabilidad.",
        "Acceso restringido.",
        "Conservación según TRD.",
      ],
      y
    );

    // ============================================================
    // 6. CONTROL Y SEGUIMIENTO / 7. REGISTROS ASOCIADOS
    // ============================================================

    y = nuevaSeccion("6. Control y Seguimiento", y);

    y = lista(
      [
        "Cumplimiento de inspecciones.",
        "Novedades recurrentes.",
        "Cierre de acciones correctivas.",
        "Indicadores del proceso.",
      ],
      y
    );

    y = subtitulo("7. Registros Asociados", y);

    y = lista(
      [
        "Formato digital de inspección.",
        "Reporte de novedades.",
        "Registro fotográfico.",
        "Historial de kilometraje.",
        "Reporte de acciones correctivas.",
        "Registro de aprobación o rechazo.",
      ],
      y
    );

    // ============================================================
    // ENCABEZADO Y PIE DE PÁGINA (en todas las páginas de contenido)
    // ============================================================

    const totalPaginas = pdf.getNumberOfPages();

    for (let pagina = 2; pagina <= totalPaginas; pagina++) {
      pdf.setPage(pagina);

      // Encabezado: nombre del documento + línea de acento
      setColorTexto(colorTextoSuave);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text(nombreDocumento, margen, 12);

      setColorRelleno(colorAcento);
      pdf.rect(margen, 15, ancho, 0.6, "F");

      // Pie de página: línea + numeración
      setColorTrazo(colorLinea);
      pdf.setLineWidth(0.3);
      pdf.line(margen, 283, margen + ancho, 283);

      setColorTexto(colorTextoSuave);
      pdf.setFontSize(8);
      pdf.text("PESV - Seguridad Vial", margen, 289);
      pdf.text(`Página ${pagina - 1} de ${totalPaginas - 1}`, margen + ancho, 289, {
        align: "right",
      });
    }

    this.mostrarPdfEnModal(pdf, nombreDocumento);
  }

  private mostrarPdfEnModal(pdf: jsPDF, nombre: string): void {
    const blob = pdf.output("blob");
    this.blobUrl = URL.createObjectURL(blob);

    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
    this.pdfNombre = nombre;
    this.mostrarModal = true;
  }

  cerrarModal(): void {
    this.mostrarModal = false;

    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.pdfUrl = null;
    this.pdfNombre = null;
  }
}